/**
 * Data Access Layer for Happening Reminders
 * Supports multiple reminders per happening
 */

import { getClient } from './client.js';
import { TABLES } from './tables.js';
import type { HappeningReminder, HappeningReminderCreate, HappeningReminderUpdate } from './types.js';

export async function getHappeningReminders(happeningId: string): Promise<HappeningReminder[]> {
  const { data, error } = await getClient()
    .from(TABLES.happening_reminders)
    .select('*')
    .eq('happening_id', happeningId)
    .order('offset_hours', { ascending: true });

  if (error) throw error;
  return (data || []) as HappeningReminder[];
}

export async function createHappeningReminder(input: HappeningReminderCreate): Promise<HappeningReminder> {
  const { data, error } = await getClient()
    .from(TABLES.happening_reminders)
    .insert(input)
    .select()
    .single();

  if (error) throw error;
  return data as HappeningReminder;
}

export async function updateHappeningReminder(id: string, input: HappeningReminderUpdate): Promise<HappeningReminder> {
  const { data, error } = await getClient()
    .from(TABLES.happening_reminders)
    .update(input)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as HappeningReminder;
}

export async function deleteHappeningReminder(id: string): Promise<void> {
  const { error } = await getClient()
    .from(TABLES.happening_reminders)
    .delete()
    .eq('id', id);

  if (error) throw error;
}

export async function deleteHappeningReminders(
  happeningId: string,
  options: { keepSent?: boolean } = {}
): Promise<void> {
  let query = getClient()
    .from(TABLES.happening_reminders)
    .delete()
    .eq('happening_id', happeningId);
  // Same reason as DeleteRemindersOptions.keepSent: a recreated row loses its sent_at,
  // and the scheduler's 24-hour "recently sent" guard goes with it.
  if (options.keepSent) query = query.is('sent_at', null);

  const { error } = await query;
  if (error) throw error;
}

/**
 * Get all happening reminders that need to be sent
 */
export async function getHappeningRemindersToSend(): Promise<Array<HappeningReminder & { happening: any; person: any }>> {
  const client = getClient();
  const now = new Date();

  // Get all reminders for happenings that have a start_time
  const { data, error } = await client
    .from(TABLES.happening_reminders)
    .select(`
      *,
      happening:happening_id (
        id,
        title,
        description,
        type,
        start_time,
        place_id,
        account_id,
        metadata
      ),
      person:person_id (
        id,
        name,
        email,
        account_id
      )
    `)
    .not('happening.start_time', 'is', null);

  if (error) throw error;

  const reminders = (data || []) as Array<HappeningReminder & { happening: any; person: any }>;

  // Filter reminders that should be sent now
  return reminders.filter((reminder) => {
    if (!reminder.happening || !reminder.happening.start_time || !reminder.person || !reminder.person.email) {
      return false;
    }

    // Parse start_time - treat as UTC if no timezone info
    let startTime: Date;
    const startTimeStr = reminder.happening.start_time;
    if (typeof startTimeStr === 'string') {
      if (startTimeStr.includes('Z') || startTimeStr.match(/[+-]\d{2}:\d{2}$/)) {
        startTime = new Date(startTimeStr);
      } else {
        startTime = new Date(startTimeStr.endsWith('Z') ? startTimeStr : startTimeStr + 'Z');
      }
    } else {
      startTime = new Date(startTimeStr);
    }
    
    // Calculate reminder time: start_time + offset_hours
    // offset_hours is negative for "before" (e.g., -24 = 1 day before)
    const reminderTime = new Date(startTime.getTime() + reminder.offset_hours * 60 * 60 * 1000);
    
    // Send if reminder time has passed (within the last hour to avoid missing it)
    const timeDiff = now.getTime() - reminderTime.getTime();
    const shouldSend = timeDiff >= 0 && timeDiff < 60 * 60 * 1000;

    // Don't send if already sent in the last 24 hours
    if (reminder.sent_at) {
      const sentAt = new Date(reminder.sent_at);
      const hoursSinceSent = (now.getTime() - sentAt.getTime()) / (1000 * 60 * 60);
      if (hoursSinceSent < 24) {
        return false;
      }
    }

    return shouldSend;
  });
}

/**
 * Mark a happening reminder as sent
 */
export async function markHappeningReminderSent(reminderId: string): Promise<void> {
  const client = getClient();
  const { error } = await client
    .from(TABLES.happening_reminders)
    .update({ sent_at: new Date().toISOString() })
    .eq('id', reminderId);

  if (error) throw error;
}
